// W-9 Tax Form Component
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import taxFormsService from '../../services/taxFormsService';

const W9Form = ({ onSubmit, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Part I - Name and Business
    name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    businessName: '',
    federalTaxClassification: 'individual', // individual, c-corp, s-corp, partnership, trust, llc, other
    llcClassification: '', // C, S, P (if LLC is selected)
    otherDescription: '', // if other is selected
    exemptPayeeCode: '',
    exemptionFromFATCA: '',
    
    // Part II - Address
    address: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Requester info (dynamically populated from agency)
    requesterName: user?.agency || '',
    requesterAddress: user?.agency_address && user?.agency_city && user?.agency_state && user?.agency_zip 
      ? `${user.agency_address}, ${user.agency_city}, ${user.agency_state} ${user.agency_zip}`
      : '',
    accountNumbers: '',
    
    // Part III - TIN
    tinType: 'ssn', // ssn or ein
    ssn1: '', ssn2: '', ssn3: '', // SSN parts
    ein1: '', ein2: '', // EIN parts
    
    // Part IV - Certification
    certification1: false, // Not subject to backup withholding
    certification2: false, // US person
    certification3: false, // Correct TIN
    certification4: false, // Not subject to FATCA
    
    // Signature
    signature: '',
    signatureDate: new Date().toISOString().split('T')[0]
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear errors for this field
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
    
    // TIN validation
    if (formData.tinType === 'ssn') {
      if (!formData.ssn1 || !formData.ssn2 || !formData.ssn3) {
        newErrors.ssn = 'Complete SSN is required';
      }
    } else {
      if (!formData.ein1 || !formData.ein2) {
        newErrors.ein = 'Complete EIN is required';
      }
    }
    
    // Certification validation
    if (!formData.certification1) newErrors.certification1 = 'This certification is required';
    if (!formData.certification2) newErrors.certification2 = 'This certification is required';
    if (!formData.certification3) newErrors.certification3 = 'This certification is required';
    
    if (!formData.signature.trim()) newErrors.signature = 'Signature is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    try {
      setLoading(true);
      setErrors({});
      
      // Prepare submission data
      const submissionData = {
        name: formData.name,
        business_name: formData.businessName,
        federal_tax_classification: formData.federalTaxClassification,
        llc_classification: formData.llcClassification,
        other_description: formData.otherDescription,
        exempt_payee_code: formData.exemptPayeeCode,
        exemption_from_fatca: formData.exemptionFromFATCA,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        tin_type: formData.tinType,
        ssn: formData.tinType === 'ssn' ? `${formData.ssn1}-${formData.ssn2}-${formData.ssn3}` : null,
        ein: formData.tinType === 'ein' ? `${formData.ein1}-${formData.ein2}` : null,
        certifications: {
          certification1: formData.certification1,
          certification2: formData.certification2,
          certification3: formData.certification3,
          certification4: formData.certification4
        },
        digital_signature: formData.signature
      };
      
      // Use the onSubmit prop if provided, otherwise fall back to direct submission
      if (onSubmit) {
        await onSubmit(submissionData);
      } else {
        await taxFormsService.submitW9(submissionData);
      }
      
      setShowSuccess(true);
      setTimeout(() => {
        if (onCancel) {
          onCancel();
        }
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: 'Failed to submit form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[var(--foreground)] mb-2">
          Form W-9: Request for Taxpayer Identification Number and Certification
        </h1>
        <p className="text-[var(--muted-foreground)]">
          Give this form to the requester. Do not send to the IRS.
        </p>
      </div>

      {showSuccess ? (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-green-800">Form submitted successfully! You will be redirected shortly.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Part I - Identification */}
          <div className="border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Part I - Taxpayer Identification</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                  1. Name (as shown on your income tax return) *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.name ? 'border-red-500' : 'border-[var(--border)]'}`}
                  required
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                  2. Business name/disregarded entity name, if different from above
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                  3. Federal tax classification *
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'individual', label: 'Individual/sole proprietor or single-member LLC' },
                    { value: 'c-corp', label: 'C Corporation' },
                    { value: 's-corp', label: 'S Corporation' },
                    { value: 'partnership', label: 'Partnership' },
                    { value: 'trust', label: 'Trust/estate' },
                    { value: 'llc', label: 'Limited liability company' },
                    { value: 'other', label: 'Other' }
                  ].map(option => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="radio"
                        name="federalTaxClassification"
                        value={option.value}
                        checked={formData.federalTaxClassification === option.value}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors.federalTaxClassification && (
                  <p className="text-red-500 text-sm mt-1">{errors.federalTaxClassification}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                    4. Exemptions (if applicable)
                  </label>
                  <input
                    type="text"
                    name="exemptPayeeCode"
                    value={formData.exemptPayeeCode}
                    onChange={handleInputChange}
                    placeholder="Exempt payee code"
                    className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                    &nbsp;
                  </label>
                  <input
                    type="text"
                    name="exemptionFromFATCA"
                    value={formData.exemptionFromFATCA}
                    onChange={handleInputChange}
                    placeholder="Exemption from FATCA code"
                    className="w-full px-3 py-2 border border-[var(--border)] rounded-md"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Part II - Address */}
          <div className="border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Part II - Address</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                  5. Address (number, street, and apt. or suite no.) *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.address ? 'border-red-500' : 'border-[var(--border)]'}`}
                  required
                />
                {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                    6. City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md ${errors.city ? 'border-red-500' : 'border-[var(--border)]'}`}
                    required
                  />
                  {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                    State *
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    maxLength="2"
                    placeholder="ST"
                    className={`w-full px-3 py-2 border rounded-md ${errors.state ? 'border-red-500' : 'border-[var(--border)]'}`}
                    required
                  />
                  {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                </div>
              </div>

              <div className="w-1/2">
                <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                  ZIP code *
                </label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  maxLength="10"
                  className={`w-full px-3 py-2 border rounded-md ${errors.zipCode ? 'border-red-500' : 'border-[var(--border)]'}`}
                  required
                />
                {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
              </div>
            </div>
          </div>

          {/* Part III - TIN */}
          <div className="border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Part III - Taxpayer Identification Number (TIN)</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                Select TIN type *
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="tinType"
                    value="ssn"
                    checked={formData.tinType === 'ssn'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span>Social Security Number (SSN)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="tinType"
                    value="ein"
                    checked={formData.tinType === 'ein'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span>Employer Identification Number (EIN)</span>
                </label>
              </div>
            </div>

            {formData.tinType === 'ssn' ? (
              <div>
                <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                  Social Security Number *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    name="ssn1"
                    value={formData.ssn1}
                    onChange={handleInputChange}
                    maxLength="3"
                    placeholder="XXX"
                    className="w-20 px-3 py-2 border border-[var(--border)] rounded-md text-center"
                  />
                  <span className="self-center">-</span>
                  <input
                    type="text"
                    name="ssn2"
                    value={formData.ssn2}
                    onChange={handleInputChange}
                    maxLength="2"
                    placeholder="XX"
                    className="w-16 px-3 py-2 border border-[var(--border)] rounded-md text-center"
                  />
                  <span className="self-center">-</span>
                  <input
                    type="text"
                    name="ssn3"
                    value={formData.ssn3}
                    onChange={handleInputChange}
                    maxLength="4"
                    placeholder="XXXX"
                    className="w-24 px-3 py-2 border border-[var(--border)] rounded-md text-center"
                  />
                </div>
                {errors.ssn && <p className="text-red-500 text-sm mt-1">{errors.ssn}</p>}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                  Employer Identification Number *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    name="ein1"
                    value={formData.ein1}
                    onChange={handleInputChange}
                    maxLength="2"
                    placeholder="XX"
                    className="w-16 px-3 py-2 border border-[var(--border)] rounded-md text-center"
                  />
                  <span className="self-center">-</span>
                  <input
                    type="text"
                    name="ein2"
                    value={formData.ein2}
                    onChange={handleInputChange}
                    maxLength="7"
                    placeholder="XXXXXXX"
                    className="w-32 px-3 py-2 border border-[var(--border)] rounded-md text-center"
                  />
                </div>
                {errors.ein && <p className="text-red-500 text-sm mt-1">{errors.ein}</p>}
              </div>
            )}
          </div>

          {/* Part IV - Certification */}
          <div className="border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Part IV - Certification</h2>
            
            <p className="text-sm text-[var(--muted-foreground)] mb-4">
              Under penalties of perjury, I certify that:
            </p>
            
            <div className="space-y-3">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="certification1"
                  checked={formData.certification1}
                  onChange={handleInputChange}
                  className="mt-1 mr-3"
                />
                <span className="text-sm">
                  1. The number shown on this form is my correct taxpayer identification number, and
                </span>
              </label>
              {errors.certification1 && <p className="text-red-500 text-sm ml-6">{errors.certification1}</p>}

              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="certification2"
                  checked={formData.certification2}
                  onChange={handleInputChange}
                  className="mt-1 mr-3"
                />
                <span className="text-sm">
                  2. I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the IRS that I am subject to backup withholding, and
                </span>
              </label>
              {errors.certification2 && <p className="text-red-500 text-sm ml-6">{errors.certification2}</p>}

              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="certification3"
                  checked={formData.certification3}
                  onChange={handleInputChange}
                  className="mt-1 mr-3"
                />
                <span className="text-sm">
                  3. I am a U.S. citizen or other U.S. person, and
                </span>
              </label>
              {errors.certification3 && <p className="text-red-500 text-sm ml-6">{errors.certification3}</p>}

              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="certification4"
                  checked={formData.certification4}
                  onChange={handleInputChange}
                  className="mt-1 mr-3"
                />
                <span className="text-sm">
                  4. The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.
                </span>
              </label>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                Signature *
              </label>
              <input
                type="text"
                name="signature"
                value={formData.signature}
                onChange={handleInputChange}
                placeholder="Type your full name as digital signature"
                className={`w-full px-3 py-2 border rounded-md ${errors.signature ? 'border-red-500' : 'border-[var(--border)]'}`}
                required
              />
              {errors.signature && <p className="text-red-500 text-sm mt-1">{errors.signature}</p>}
              
              <p className="text-sm text-[var(--muted-foreground)] mt-2">
                Date: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-3 bg-[var(--muted-foreground)] text-white rounded-md hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-[var(--border)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-3 bg-[var(--primary)] text-white rounded-md hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Submitting...' : 'Submit W-9 Form'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default W9Form;
